import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { FadeUp } from "@/components/Reveal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:border-royal focus:outline-none focus:ring-2 focus:ring-royal/20";

const FinalCTA = () => {
    const { content } = useSite();
    const { finalCta } = content;
    const [form, setForm] = useState({ name: "", email: "", company: "", teamSize: "", message: "" });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");

    const set = (key) => (e) => {
        setForm({ ...form, [key]: e.target.value });
        if (errors[key]) setErrors({ ...errors, [key]: null });
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = "Please enter your name.";
        if (!EMAIL_RE.test(form.email)) next.email = "Please enter a valid work email.";
        if (!form.company.trim()) next.company = "Please enter your company.";
        if (!form.teamSize) next.teamSize = "Select your team size.";
        if (form.message.trim().length < 10) next.message = "Tell us a little about what you want to fix (10+ characters).";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setStatus("sending");
        try {
            await api.post("/enquiries", {
                name: form.name.trim(),
                email: form.email.trim(),
                company: form.company.trim(),
                service: `Team size: ${form.teamSize}`,
                message: form.message.trim(),
            });
            setStatus("success");
            toast.success("Request received — we will reply within one business day.");
        } catch (err) {
            setStatus("idle");
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    return (
        <section id="contact" data-testid="final-cta-section" className="bg-navy py-20 sm:py-28">
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                <FadeUp>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amberct">Book a Free Discovery Call</p>
                    <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                        {finalCta.heading}
                    </h2>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg">{finalCta.sub}</p>
                    <ul className="mt-8 space-y-3 text-sm text-slate-300">
                        <li className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" /> 30 minutes, no pitch deck, no obligation
                        </li>
                        <li className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" /> We map your sales motion live on the call
                        </li>
                        <li className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" /> Honest verdict — even if the answer is "don't build"
                        </li>
                    </ul>
                    <div className="mt-10 space-y-2.5 text-sm">
                        <a href={`mailto:${content.contact.email}`} data-testid="cta-email-link" className="flex items-center gap-3 text-slate-300 transition-colors hover:text-white">
                            <Mail className="h-4 w-4 text-amberct" /> {content.contact.email}
                        </a>
                        <a href={`tel:${content.contact.phone.replace(/\s/g, "")}`} data-testid="cta-phone-link" className="flex items-center gap-3 text-slate-300 transition-colors hover:text-white">
                            <Phone className="h-4 w-4 text-amberct" /> {content.contact.phone}
                        </a>
                    </div>
                </FadeUp>

                <FadeUp delay={0.15}>
                    <div className="rounded-2xl bg-white p-7 shadow-2xl sm:p-9">
                        {status === "success" ? (
                            <div data-testid="contact-success-state" className="flex min-h-[24rem] flex-col items-center justify-center text-center">
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                                </span>
                                <h3 className="mt-5 font-display text-2xl font-extrabold text-navy">Request received.</h3>
                                <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-600">
                                    Thanks, {form.name.split(" ")[0]}. We will reply to {form.email} within one business day to schedule your call.
                                </p>
                                <button
                                    type="button"
                                    data-testid="contact-send-another-button"
                                    onClick={() => { setForm({ name: "", email: "", company: "", teamSize: "", message: "" }); setStatus("idle"); }}
                                    className="mt-6 text-sm font-bold text-royal hover:underline"
                                >
                                    Send another request
                                </button>
                            </div>
                        ) : (
                            <form data-testid="contact-form" onSubmit={submit} noValidate>
                                <h3 className="font-display text-xl font-extrabold text-navy">Book your free discovery call</h3>
                                <div className="mt-6 space-y-4">
                                    <div>
                                        <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Name</label>
                                        <input id="contact-name" data-testid="contact-name-input" type="text" autoComplete="name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
                                        {errors.name && <p data-testid="contact-name-error" className="mt-1 text-xs font-medium text-red-600">{errors.name}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Work Email</label>
                                        <input id="contact-email" data-testid="contact-email-input" type="email" autoComplete="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                                        {errors.email && <p data-testid="contact-email-error" className="mt-1 text-xs font-medium text-red-600">{errors.email}</p>}
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="contact-company" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Company</label>
                                            <input id="contact-company" data-testid="contact-company-input" type="text" autoComplete="organization" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" />
                                            {errors.company && <p data-testid="contact-company-error" className="mt-1 text-xs font-medium text-red-600">{errors.company}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="contact-team-size" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Team Size</label>
                                            <select id="contact-team-size" data-testid="contact-team-size-select" value={form.teamSize} onChange={set("teamSize")} className={inputCls}>
                                                <option value="">Select…</option>
                                                {finalCta.teamSizes.map((size) => (
                                                    <option key={size} value={size}>{size}</option>
                                                ))}
                                            </select>
                                            {errors.teamSize && <p data-testid="contact-team-size-error" className="mt-1 text-xs font-medium text-red-600">{errors.teamSize}</p>}
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">What are you trying to fix?</label>
                                        <textarea id="contact-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="e.g. Leads come in over WhatsApp and email, and follow-ups depend on whoever remembers…" />
                                        {errors.message && <p data-testid="contact-message-error" className="mt-1 text-xs font-medium text-red-600">{errors.message}</p>}
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="contact-submit-button"
                                        disabled={status === "sending"}
                                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-amberct px-6 py-4 text-base font-bold text-navy shadow-lg shadow-amberct/20 transition-all hover:-translate-y-0.5 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {status === "sending" ? (
                                            <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
                                        ) : (
                                            <>Book a Free Discovery Call <ArrowRight className="h-5 w-5" /></>
                                        )}
                                    </button>
                                    <p className="text-center text-xs text-slate-400">No spam, no obligation. Your details stay with us.</p>
                                </div>
                            </form>
                        )}
                    </div>
                </FadeUp>
            </div>
        </section>
    );
};

export default FinalCTA;
